import React, { useState, useMemo } from 'react';
import './SupportedMls.css';
import Icon from './Icon';
import { scopedKey } from './scope';

function SupportedMls({ scope, onManageMls, onSaved, onDeleteMls, accounts = [], orgs = [],
  apiMode = false, catalog = null, onCreate, onUpdate }) {
  const K_MLS = scopedKey('ira_mls', scope);   // each organization manages its own MLS list
  const isSystem = scope === 'system';         // only the system catalog has a Manage page
  // In API mode the catalog is owned by the server (passed in via `catalog`); otherwise
  // it lives in this browser's localStorage.
  const [localList, setLocalList] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_MLS) || '[]'); } catch { return []; }
  });
  const list = apiMode ? (catalog || []) : localList;
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName]     = useState('');
  const [desc, setDesc]     = useState('');
  const [error, setError]   = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected]   = useState(() => new Set());
  const [deleteId, setDeleteId]   = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [editId, setEditId]       = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', description: '' });

  function persist(next) { setLocalList(next); localStorage.setItem(K_MLS, JSON.stringify(next)); if (onSaved) onSaved(next); }

  const q = search.toLowerCase().trim();
  const filtered = useMemo(() =>
    q ? list.filter(m =>
          (m.name || '').toLowerCase().includes(q) ||
          (m.description || '').toLowerCase().includes(q))
      : list,
    [list, q]);

  const allSelected = filtered.length > 0 && filtered.every(m => selected.has(m.id));
  const someSelected = filtered.some(m => selected.has(m.id));

  async function add() {
    const n = name.trim();
    const d = desc.trim();
    if (!n) { setError('MLS Name is required.'); return; }
    if (!d) { setError('MLS Description is required.'); return; }
    if (list.some(m => m.name.toLowerCase() === n.toLowerCase())) { setError('That MLS name already exists.'); return; }
    if (apiMode) {
      try { await onCreate({ name: n, description: d }); }
      catch (e) { setError(e.message || 'Could not add MLS.'); return; }
    } else {
      const id = `mls_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      persist([...list, { id, name: n, description: d }]);
    }
    setName(''); setDesc(''); setShowAdd(false); setError(''); setNotice(`Added "${n}".`);
  }

  function toggleSelect(id) {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }
  function toggleSelectAll() {
    setSelected(prev => {
      const s = new Set(prev);
      const all = filtered.length > 0 && filtered.every(m => s.has(m.id));
      filtered.forEach(m => all ? s.delete(m.id) : s.add(m.id));
      return s;
    });
  }
  function doDelete(id) {
    if (!apiMode) persist(list.filter(m => m.id !== id));   // localStorage: remove locally (fires onSaved)
    if (onDeleteMls) onDeleteMls(id);          // cascade-delete every org/agent + data (API: also refreshes catalog)
    setSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
    setDeleteId(null);
    setNotice('MLS deleted — along with all its organizations, agents, and their data.');
  }
  function doBulkDelete() {
    const ids = new Set(selected);
    if (!apiMode) persist(list.filter(m => !ids.has(m.id)));
    if (onDeleteMls) onDeleteMls([...ids]);     // cascade-delete everything under each removed MLS
    setNotice(`Deleted ${ids.size} MLS — along with all their organizations, agents, and data.`);
    setSelected(new Set());
    setBulkConfirm(false);
  }
  // How many orgs / agents live under an MLS — shown in the delete confirmation so the
  // operator knows the blast radius before removing it.
  function impactOf(mlsId) {
    const orgIds = new Set((orgs || []).filter(o => o.mlsId === mlsId).map(o => o.id));
    // Every non-system account the cascade will remove: members of those orgs (incl.
    // org admins) plus any independent agents attached directly to the MLS.
    const agentCount = (accounts || []).filter(a => !a.system
      && ((a.orgId && orgIds.has(a.orgId)) || a.mlsId === mlsId || a.mls === mlsId)).length;
    return { orgs: orgIds.size, agents: agentCount };
  }

  function startEdit(m) { setEditId(m.id); setEditDraft({ name: m.name, description: m.description || '' }); setError(''); }
  async function saveEdit() {
    const n = editDraft.name.trim();
    const d = editDraft.description.trim();
    if (!n) { setError('MLS Name is required.'); return; }
    if (!d) { setError('MLS Description is required.'); return; }
    if (list.some(m => m.id !== editId && m.name.toLowerCase() === n.toLowerCase())) { setError('That MLS name already exists.'); return; }
    if (apiMode) {
      try { await onUpdate(editId, { name: n, description: d }); }
      catch (e) { setError(e.message || 'Could not update MLS.'); return; }
    } else {
      persist(list.map(m => m.id === editId ? { ...m, name: n, description: d } : m));
    }
    setEditId(null); setError(''); setNotice('MLS updated.');
  }

  return (
    <div className="mls-container">
      <div className="mls-header">
        <div>
          <h2>Supported MLS</h2>
          <p>The MLS systems your platform supports. Add, search, or remove entries.</p>
        </div>
        <button className="mls-add-btn" onClick={() => { setShowAdd(s => !s); setError(''); }}><Icon name="add" size={16} /> Add MLS</button>
      </div>

      {notice && <div className="mls-notice">{notice} <button className="mls-notice-x" onClick={() => setNotice('')}>✕</button></div>}

      {showAdd && (
        <div className="mls-add-panel">
          <div className="mls-add-title">New MLS</div>
          {error && <div className="mls-error">⚠ {error}</div>}
          <div className="mls-field">
            <label className="mls-label">MLS Name <span className="mls-req">*</span></label>
            <input className="mls-input" value={name} autoFocus
              onChange={e => { setName(e.target.value); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') add(); }} />
          </div>
          <div className="mls-field">
            <label className="mls-label">MLS Description <span className="mls-req">*</span></label>
            <textarea className="mls-input mls-textarea" rows={2} value={desc}
              onChange={e => { setDesc(e.target.value); setError(''); }} />
          </div>
          <div className="mls-add-actions">
            <button className="rt-save-btn" onClick={add}>Add MLS</button>
            <button className="contacts-import-cancel-btn" onClick={() => { setShowAdd(false); setName(''); setDesc(''); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="mls-toolbar">
        <input className="mls-search" placeholder="Filter by MLS name or description…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <span className="mls-count">{filtered.length} of {list.length}</span>
      </div>

      {selected.size > 0 && (
        <div className="mls-bulk-bar">
          <span className="mls-bulk-count">{selected.size} selected</span>
          {bulkConfirm ? (
            <>
              {(() => {
                const imp = [...selected].reduce((a, id) => { const i = impactOf(id); return { orgs: a.orgs + i.orgs, agents: a.agents + i.agents }; }, { orgs: 0, agents: 0 });
                return <span className="mls-bulk-confirm">Delete {selected.size} MLS{imp.orgs ? ` and all ${imp.orgs} org(s) + ${imp.agents} agent(s) under them` : ''}? This cannot be undone.</span>;
              })()}
              <button className="mls-bulk-del" onClick={doBulkDelete}>Delete</button>
              <button className="contacts-import-cancel-btn" onClick={() => setBulkConfirm(false)}>Cancel</button>
            </>
          ) : (
            <button className="mls-bulk-del" onClick={() => setBulkConfirm(true)}><Icon name="delete" size={16} /> Delete Selected</button>
          )}
          <button className="contacts-import-cancel-btn" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {list.length === 0 ? (
        <div className="mls-empty"><span><Icon name="mls" size={18} /></span><p>No MLS added yet. Click “+ Add MLS” to get started.</p></div>
      ) : filtered.length === 0 ? (
        <div className="mls-empty"><span><Icon name="search" size={18} /></span><p>No MLS match “{search}”.</p></div>
      ) : (
        <div className="mls-table-wrap">
          <table className="mls-table">
            <thead>
              <tr>
                <th className="mls-col-select">
                  <input type="checkbox" title="Select all"
                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th>MLS Name</th>
                <th>MLS Description</th>
                <th className="mls-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <React.Fragment key={m.id}>
                <tr className={selected.has(m.id) ? 'mls-row-selected' : ''}>
                  <td className="mls-col-select">
                    <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} />
                  </td>
                  {editId === m.id ? (
                    <>
                      <td><input className="mls-input" value={editDraft.name}
                        onChange={e => { setEditDraft(d => ({ ...d, name: e.target.value })); setError(''); }} /></td>
                      <td><input className="mls-input" value={editDraft.description}
                        onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))} /></td>
                      <td className="mls-actions">
                        <button className="mls-save-row" onClick={saveEdit}>Save</button>
                        <button className="contacts-import-cancel-btn" onClick={() => { setEditId(null); setError(''); }}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="mls-name" data-label="MLS Name">{m.name}</td>
                      <td className="mls-desc" data-label="Description">{m.description || '—'}</td>
                      <td className="mls-actions" data-label="Actions">
                        {isSystem && onManageMls && (
                          <button className="mls-manage-btn" title="Manage" onClick={() => onManageMls(m.id)}>
                            <span className="lbl-full"><Icon name="process" size={16} /> Manage</span><span className="lbl-short"><Icon name="process" size={16} /></span>
                          </button>
                        )}
                        <button className="mls-edit-btn" title="Edit" onClick={() => startEdit(m)}>
                          <span className="lbl-full">Edit</span><span className="lbl-short"><Icon name="rename" size={16} /></span>
                        </button>
                        {deleteId === m.id ? (
                          <>
                            {(() => { const i = impactOf(m.id); return i.orgs > 0
                              ? <span style={{ color: '#b91c1c', fontSize: '0.8em', marginRight: 6 }}>⚠ Deletes {i.orgs} org(s) + {i.agents} agent(s) and all their data</span>
                              : null; })()}
                            <button className="mls-del-confirm" onClick={() => doDelete(m.id)}>Confirm</button>
                            <button className="contacts-import-cancel-btn" onClick={() => setDeleteId(null)}>Cancel</button>
                          </>
                        ) : (
                          <button className="mls-del-btn" title="Delete" onClick={() => setDeleteId(m.id)}>
                            <span className="lbl-full">Delete</span><span className="lbl-short"><Icon name="delete" size={16} /></span>
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editId && error && <div className="mls-error mls-error-float">⚠ {error}</div>}
    </div>
  );
}

export default SupportedMls;
